'use client';

import { useBunja } from 'bunja/react';
import { isSameDay } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { Fragment, useEffect } from 'react';

import { Notification } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Spinner } from '@flipster/react/design-system/spinner';
import AlertIcon from '@flipster/react/icon/generated/AlertIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import DocumentSearchIcon from '@flipster/react/icon/generated/DocumentSearchIcon';
import RocketIcon from '@flipster/react/icon/generated/RocketIcon';
import SettingIcon from '@flipster/react/icon/generated/SettingIcon';
import { Trigger } from '@flipster/react/misc/browser/intersection';
import { css } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { convertDate } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { FeatureGimmickEvent } from '@flipster/utils/segment/events/feature-gimmick';

import { notificationCenterBunja } from './bunja';
import { NotificationItem } from './notification-item';

interface NotificationListProps {
  onSettingsClick: () => void;
}

const groupNotificationsByDate = (notifications: Notification[]) => {
  const groups = notifications.reduce(
    (acc, notification) => {
      const notificationDate = convertDate(notification.createdAt);
      if (!notificationDate) return acc;
      const today = new Date();
      const currentYear = today.getFullYear();
      const notificationYear = notificationDate.getFullYear();

      let groupKey: string;

      if (isSameDay(notificationDate, today)) {
        groupKey = 'Today';
      } else {
        const monthName = notificationDate.toLocaleDateString('en-US', { month: 'short' });
        if (notificationYear < currentYear) {
          groupKey = `${notificationYear} ${monthName}`;
        } else {
          groupKey = monthName;
        }
      }

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(notification);
      return acc;
    },
    {} as Record<string, Notification[]>,
  );

  return Object.entries(groups);
};

export const NotificationList = ({ onSettingsClick }: NotificationListProps) => {
  const {
    notificationsAtom,
    hasNextPageAtom,
    isFetchingNextPageAtom,
    fetchNextPage,
    isLoadingAtom,
    isErrorAtom,
    newFeatureCountAtom,
    isNotificationCenterOpenAtom,
  } = useBunja(notificationCenterBunja);

  const notifications = useAtomValue(notificationsAtom);
  const isFetchingNextPage = useAtomValue(isFetchingNextPageAtom);
  const hasNextPage = useAtomValue(hasNextPageAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const isError = useAtomValue(isErrorAtom);
  const newFeatureCount = useAtomValue(newFeatureCountAtom);
  const setIsNotificationCenterOpen = useSetAtom(isNotificationCenterOpenAtom);

  const triggerNextPage = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  useEffect(() => {
    if (newFeatureCount > 0) {
      segment.track(FeatureGimmickEvent.NotificationCenterNewFeatureUpdateImpression, {
        feature_update_count: newFeatureCount,
      });
    }
  }, [newFeatureCount]);

  const handleNewFeatureClick = () => {
    segment.track(FeatureGimmickEvent.NotificationCenterNewFeatureUpdateClick, {
      feature_update_count: newFeatureCount,
    });
    setIsNotificationCenterOpen(false);
  };

  return (
    <div
      className={vstack({
        h: '70vh',
        w: '100%',
        gap: 20,
      })}
    >
      <div
        className={hstack({
          w: '100%',
          alignItems: 'flex-start',
        })}
      >
        <p
          className={css({
            textStyle: 'Title/M',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Autogen_common.r251126.s_Notification_center' />
        </p>
        <IconButton
          css={{
            marginLeft: 'auto',
          }}
          hierarchy='quaternary'
          size='small'
          color='content.default.level-5'
          icon={<SettingIcon />}
          onClick={onSettingsClick}
        />
      </div>
      {newFeatureCount > 0 && (
        <Link
          href='/feature-updates'
          onClick={handleNewFeatureClick}
          className={hstack({
            gap: 12,
            py: 16,
            px: 12,
            w: '100%',
            borderColor: 'border.accent.info',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 8,
            color: 'content.accent.info',
            textDecoration: 'none',
            _hover: {
              bgColor: 'background.default.level-3',
            },
          })}
        >
          <RocketIcon size={24} />
          <p
            className={css({
              textStyle: 'Text/L/Strong',
              color: 'content.default.level-5',
              flex: 1,
            })}
          >
            <FormattedMessage id='Common.r251203.New_feature_update' />
          </p>
          <div className={hstack({ gap: 8 })}>
            <Badge type='tint' color='accent'>
              {newFeatureCount}
            </Badge>
            <IconButton hierarchy='quaternary' size='small' icon={<ChevronRightIcon />} />
          </div>
        </Link>
      )}
      <div
        className={css({
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          flex: 1,
          minH: 0,
          pb: 20,
        })}
      >
        {isLoading ? (
          <div className={center({ h: '100%' })}>
            <Spinner />
          </div>
        ) : isError ? (
          <div className={center({ h: '100%' })}>
            <div
              className={vstack({
                gap: 3,
                alignItems: 'center',
                textAlign: 'center',
                textStyle: 'Text/M/Regular',
              })}
            >
              <DocumentSearchIcon size={32} />
              <p>
                Can't load notifications. <br />
                Please try again.
              </p>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className={center({ h: '100%' })}>
            <div
              className={vstack({
                gap: 3,
                alignItems: 'center',
                textAlign: 'center',
                textStyle: 'Text/M/Regular',
              })}
            >
              <AlertIcon size={32} color={token('colors.content.default.level-2')} />
              <p>
                <FormattedMessage id='Autogen_common.r251126.s_No_notifications_yet' />
              </p>
            </div>
          </div>
        ) : (
          <div className={vstack({ alignItems: 'stretch' })}>
            {groupedNotifications.map(([date, notifications]) => (
              <div key={date} className={vstack({ gap: 12, alignItems: 'stretch' })}>
                <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>{date}</div>
                <div>
                  {notifications.map((notification, idx) => (
                    <Fragment key={notification.id}>
                      {idx === notifications.length - 5 && <Trigger onTrigger={triggerNextPage} />}
                      <NotificationItem {...notification} listOrder={idx} />
                    </Fragment>
                  ))}
                </div>
              </div>
            ))}
            {isFetchingNextPage && (
              <div className={center({ h: 20 })}>
                <Spinner size={20} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
